import { useState, useEffect, useCallback } from 'react';
import { canPerformOfflineOperation, getOfflineStatus } from '../services/offlineSubscriptionService';

/**
 * Hook for managing online operations
 * @param {string} operation - The operation type (orders, expenses, billing)
 * @returns {Object} - Operation status and utilities
 */
export function useOfflineOperations(operation) {
  const [status, setStatus] = useState({
    isAllowed: true,
    isOnline: typeof navigator !== 'undefined' ? navigator.onLine : true,
    message: 'Online - all operations available',
    cacheValid: true
  });

  const [loading, setLoading] = useState(false);

  const evaluateOfflinePermission = useCallback(async () => {
    if (typeof navigator !== 'undefined' && navigator.onLine) {
      setStatus({
        isAllowed: true,
        isOnline: true,
        message: 'Online - all operations available',
        cacheValid: true
      });
      return;
    }

    setLoading(true);
    try {
      const result = await canPerformOfflineOperation(operation);
      setStatus({
        isAllowed: result.allowed,
        isOnline: false,
        message: result.reason || (result.allowed ? 'Offline operation allowed' : 'Offline operation not allowed'),
        cacheValid: result.allowed
      });
    } catch (error) {
      setStatus({
        isAllowed: false,
        isOnline: false,
        message: 'Offline - operation not available',
        cacheValid: false
      });
    } finally {
      setLoading(false);
    }
  }, [operation]);

  useEffect(() => {
    const init = async () => {
      if (typeof navigator !== 'undefined' && navigator.onLine) {
        setStatus({
          isAllowed: true,
          isOnline: true,
          message: 'Online - all operations available',
          cacheValid: true
        });
      } else {
        await evaluateOfflinePermission();
      }
    };

    init();

    const handleOnline = () => {
      setStatus({
        isAllowed: true,
        isOnline: true,
        message: 'Online - all operations available',
        cacheValid: true
      });
    };

    const handleOffline = () => {
      evaluateOfflinePermission();
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [evaluateOfflinePermission]);

  useEffect(() => {
    const loadStatus = async () => {
      const offlineStatus = await getOfflineStatus();
      setStatus((prev) => ({
        ...prev,
        cacheValid: offlineStatus.cacheValid,
        message: prev.isOnline ? prev.message : offlineStatus.message
      }));
    };
    loadStatus();
  }, []);

  return {
    offlineStatus: status,
    loading,
    canPerformOperation: status.isAllowed,
    isOnline: status.isOnline,
    message: status.message,
    showOfflineWarning: !status.isOnline || !status.isAllowed,
    refresh: evaluateOfflinePermission
  };
}